import type { VariantProps } from "class-variance-authority"
import type { UnitStatus } from "@/mappers/status-colors.mapper"

import { Slot } from "@radix-ui/react-slot"
import Link from "next/link"

import * as React from "react"
import { cn } from "@/lib/utils"
import { getInlineStylesForStatus } from "@/mappers/status-colors.mapper"
import { badgeVariants } from "./badge-variants"

function Badge({
  className,
  variant,
  status,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean
    status?: UnitStatus | string
  }) {
  const Comp = asChild ? Slot : "span"

  // Si hay status, usar colores específicos del mapper
  if (status) {
    // Definir colores inline para máxima especificidad
    const inlineStyles = getInlineStylesForStatus(status as UnitStatus)

    return (
      <Comp
        className={cn(
          // Solo las clases base sin colores
          "inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none overflow-hidden",
          // NO aplicar badgeVariants que contienen colores de tema
          className,
        )}
        data-slot="badge"
        style={inlineStyles}
        {...props}
      />
    )
  }
  // Si no hay status, usar variantes normales
  return (
    <Comp
      className={cn(badgeVariants({ variant }), className)}
      data-slot="badge"
      {...props}
    />
  )
}

function BadgeLink({ href, children, ...props }: React.ComponentProps<"a"> & { href: string } & VariantProps<typeof badgeVariants>) {
  return (
    <Badge asChild {...props}>
      <Link href={href} target="_blank">
        {children}
      </Link>
    </Badge>
  )
}

export { Badge, BadgeLink }
